/**
 * The skills the composer's `$` menu offers: the organization's own library
 * (its skills, then its jurisdictions' and the shared ones, as the Skills page
 * lists them) ahead of the agent's built-in skills. Selecting one inserts
 * `$<slug>`; the server resolves a library slug to the skill's method for that
 * turn (`amoreg/taggedSkills`), and the agent loads a built-in one by name.
 *
 * @module components/chat/composerSkills
 */
import type { ServerProviderSkill } from "@amoreg/contracts";

import type { SkillView } from "../../state/connections";

const SCOPE_ORDER: Record<SkillView["scope"], number> = { organization: 0, jurisdiction: 1, global: 2 };

export function libraryComposerSkill(skill: SkillView): ServerProviderSkill | null {
  const name = skill.slug.trim();
  if (!name) return null;
  const description = skill.description.trim();
  return {
    name,
    displayName: skill.name.trim() || name,
    ...(description ? { description, shortDescription: description } : {}),
    path: `skills/${skill.scope}/${name}`,
    // The source icon: the organization's own skills read as its workspace's,
    // jurisdiction and shared ones as Amoreg's.
    scope: skill.scope === "organization" ? "workspace" : "system",
    enabled: true,
  };
}

export function composerSkills(
  providerSkills: ReadonlyArray<ServerProviderSkill>,
  librarySkills: ReadonlyArray<SkillView>,
): ReadonlyArray<ServerProviderSkill> {
  const library = [...librarySkills]
    .sort((a, b) => SCOPE_ORDER[a.scope] - SCOPE_ORDER[b.scope])
    .map(libraryComposerSkill)
    .filter((skill): skill is ServerProviderSkill => skill !== null);
  const seen = new Set<string>();
  const merged: ServerProviderSkill[] = [];
  for (const skill of [...library, ...providerSkills]) {
    if (seen.has(skill.name)) continue;
    seen.add(skill.name);
    merged.push(skill);
  }
  return merged;
}
